import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Search, Loader2, Pencil, RotateCcw, Layers, CalendarDays, Scissors, Ruler, Timer, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { pt } from "date-fns/locale";

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function DateField({ value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const sel = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[140px] justify-start font-normal" data-testid={testid}>
          <CalendarDays className="h-4 w-4 mr-2 text-muted-foreground" />
          {value ? fmtDate(value) : <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={pt} weekStartsOn={1} selected={sel} defaultMonth={sel}
          onSelect={(d) => { onChange(d ? isoOf(d) : ""); setOpen(false); }} />
        {value && (
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" className="w-full" data-testid={`${testid}-clear`} onClick={() => { onChange(""); setOpen(false); }}>Limpar data</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
import {
  listProductionOrders, getProductionOrder, saveProductionOrderMeta,
  getProductionOrderSettings, saveProductionOrderSettings,
} from "@/lib/api";

const STATUS = {
  em_curso: { label: "Em curso", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  pausada: { label: "Pausada", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  concluida: { label: "Concluída", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};
const TYPE = {
  of: { label: "OF", cls: "bg-primary/10 text-primary border-primary/20" },
  of_group: { label: "Grupo OF", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  no_of: { label: "Sem OF", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const GROUPING_LABELS = {
  first_3_words: "As 3 primeiras palavras",
  before_plan: "Tudo antes do nº de plano",
  first_word: "A primeira palavra",
};

export function fmtMin(m) {
  const v = Number(m || 0);
  const h = Math.floor(v / 60);
  const mm = Math.round(v - h * 60);
  if (h === 0) return `${mm} min`;
  return `${h}h${String(mm).padStart(2, "0")}`;
}
const fmtM = (v) => `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 1 })} m`;
function fmtDate(d) {
  if (!d) return "—";
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y}`;
}
function period(a, b) {
  if (!a) return "—";
  return a === b ? fmtDate(a) : `${fmtDate(a)} → ${fmtDate(b)}`;
}

function StatCard({ label, value, hint, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums" style={{ fontFamily: "Space Grotesk" }}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </Card>
  );
}

function progressColor(p) {
  if (p >= 100) return "bg-emerald-500";
  if (p >= 75) return "bg-sky-500";
  if (p >= 40) return "bg-amber-500";
  return "bg-slate-400";
}

function ProgressBar({ pct, testid, big = false }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-muted-foreground">—</span>;
  const w = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex items-center gap-2 min-w-[120px]" data-testid={testid}>
      <div className={`flex-1 rounded-full bg-muted overflow-hidden ${big ? "h-3" : "h-2"}`}>
        <div className={`h-full rounded-full transition-all ${progressColor(pct)}`} style={{ width: `${w}%` }} />
      </div>
      <span className={`tabular-nums font-medium ${big ? "text-base" : "text-xs"} ${pct >= 100 ? "text-emerald-600" : ""}`}>
        {pct.toLocaleString("pt-PT", { maximumFractionDigits: 1 })}%
      </span>
    </div>
  );
}

function Pill({ map, k }) {
  const s = map[k] || { label: k, cls: "" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

export default function ProductionOrdersPage() {
  const [tab, setTab] = useState("of");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grouping, setGrouping] = useState("first_3_words");
  const [openKey, setOpenKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: tab };
      if (status !== "all") params.status = status;
      if (start) params.start = start;
      if (end) params.end = end;
      setData(await listProductionOrders(params));
    } catch {
      toast.error("Falha ao carregar ordens de fabrico");
    } finally {
      setLoading(false);
    }
  }, [tab, status, start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getProductionOrderSettings().then((s) => setGrouping(s.no_of_grouping)).catch(() => {}); }, []);

  async function changeGrouping(v) {
    setGrouping(v);
    try {
      await saveProductionOrderSettings({ no_of_grouping: v });
      toast.success("Agrupamento dos cortes sem OF atualizado");
      load();
    } catch {
      toast.error("Falha ao guardar agrupamento");
    }
  }

  const items = useMemo(() => {
    const list = data?.items || [];
    if (!q.trim()) return list;
    const n = q.trim().toLowerCase();
    return list.filter((i) => `${i.name} ${i.default_name} ${i.sample_description || ""} ${i.ofs.join(" ")}`.toLowerCase().includes(n));
  }, [data, q]);
  const sm = data?.summary;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
          <ClipboardList className="h-7 w-7 text-primary" /> Ordens de Fabrico
        </h1>
        <p className="text-sm text-muted-foreground">
          Cortes agrupados pela OF encontrada na descrição (padrão <span className="font-mono">0000.26</span>). Cortes sem OF são agrupados pelo início da descrição.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="po-stat-of" label="Ordens de fabrico" value={sm?.of_count ?? "—"} hint={`${sm?.cuts_with_of ?? 0} cortes`} />
        <StatCard testid="po-stat-em-curso" label="Em curso" value={sm?.em_curso ?? "—"} />
        <StatCard testid="po-stat-concluida" label="Concluídas" value={sm?.concluida ?? "—"} />
        <StatCard testid="po-stat-no-of" label="Grupos sem OF" value={sm?.no_of_count ?? "—"} hint={`${sm?.cuts_without_of ?? 0} cortes`} />
      </div>

      <Card className="p-3 sm:p-4 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="of" data-testid="po-tab-of">Com OF</TabsTrigger>
              <TabsTrigger value="no_of" data-testid="po-tab-no-of">Sem OF</TabsTrigger>
              <TabsTrigger value="all" data-testid="po-tab-all">Todas</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="po-search-input" placeholder="Procurar OF, nome ou descrição…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[150px]" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos estados</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <DateField value={start} onChange={setStart} placeholder="Desde" testid="po-start-date" />
            <span className="text-muted-foreground text-sm">a</span>
            <DateField value={end} onChange={setEnd} placeholder="Até" testid="po-end-date" />
          </div>
        </div>
        {tab !== "of" && (
          <div className="flex flex-wrap items-center gap-2 text-sm border-t pt-3">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Agrupar cortes sem OF por:</span>
            <Select value={grouping} onValueChange={changeGrouping}>
              <SelectTrigger className="w-[240px] h-8" data-testid="po-grouping-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(GROUPING_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">Os nomes amigáveis ficam guardados para cada modo de agrupamento.</span>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="po-table">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Ordem / Grupo</th>
                <th className="text-left px-3 py-3 font-medium">Tipo</th>
                <th className="text-right px-3 py-3 font-medium">Cortes</th>
                <th className="text-right px-3 py-3 font-medium">Peças</th>
                <th className="text-right px-3 py-3 font-medium">T. corte</th>
                <th className="text-right px-3 py-3 font-medium">Paragem</th>
                <th className="text-right px-3 py-3 font-medium">Perímetro</th>
                <th className="text-left px-3 py-3 font-medium">Objetivo</th>
                <th className="text-left px-3 py-3 font-medium">Período</th>
                <th className="text-left px-3 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="py-12 text-center text-muted-foreground" data-testid="po-empty">
                  Sem cortes registados para estes filtros. Os cortes aparecem aqui assim que a recipe alimentar a Máquina de Corte.
                </td></tr>
              ) : items.map((i) => (
                <tr key={i.key} onClick={() => setOpenKey(i.key)} className="border-t hover:bg-muted/40 cursor-pointer" data-testid={`po-row-${i.key}`}>
                  <td className="px-4 py-3 max-w-[420px]">
                    <div className="font-medium flex items-center gap-1.5">
                      {i.name}
                      {i.custom_name && <Pencil className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate" title={i.sample_description}>
                      {i.custom_name && i.type !== "no_of" ? `${i.default_name} · ` : ""}{i.sample_description}
                    </div>
                  </td>
                  <td className="px-3 py-3"><Pill map={TYPE} k={i.type} /></td>
                  <td className="px-3 py-3 text-right tabular-nums">{i.cuts_count}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{i.pieces_count}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{fmtMin(i.cutting_time)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-red-600">{fmtMin(i.machine_stopped)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{fmtM(i.perimeter)}</td>
                  <td className="px-3 py-3">
                    <ProgressBar pct={i.progress_pct} testid={`po-progress-${i.key}`} />
                    {i.target_perimeter ? <div className="text-[11px] text-muted-foreground mt-0.5">de {fmtM(i.target_perimeter)}</div> : null}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">{period(i.first_date, i.last_date)}<div className="text-muted-foreground">{i.days_count} dia(s)</div></td>
                  <td className="px-3 py-3"><Pill map={STATUS} k={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <OrderDetailDialog orderKey={openKey} range={{ start, end }} onClose={() => setOpenKey(null)} onSaved={load} />
    </div>
  );
}

function OrderDetailDialog({ orderKey, range, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("em_curso");
  const [notes, setNotes] = useState("");
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orderKey) { setD(null); return; }
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    getProductionOrder(orderKey, params)
      .then((r) => { setD(r); setName(r.custom_name || ""); setStatus(r.status); setNotes(r.notes || ""); setTarget(r.target_perimeter ? String(r.target_perimeter) : ""); })
      .catch(() => toast.error("Falha ao carregar detalhe"))
      .finally(() => setLoading(false));
  }, [orderKey, range.start, range.end]);

  async function save(extra = {}) {
    setSaving(true);
    try {
      const tgt = parseFloat(String(target).replace(",", "."));
      if (target !== "" && (Number.isNaN(tgt) || tgt < 0)) { toast.error("Metros previstos inválidos"); setSaving(false); return; }
      await saveProductionOrderMeta({ key: orderKey, name, status, notes, target_perimeter: target === "" ? 0 : tgt, ...extra });
      toast.success("Ordem atualizada");
      if (extra.name === "") setName("");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!orderKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="po-detail-dialog">
        {loading || !d ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5 min-w-0">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2" data-testid="po-detail-title">
                {d.name} <Pill map={TYPE} k={d.type} /> <Pill map={STATUS} k={d.status} />
              </DialogTitle>
              <DialogDescription>
                {d.type === "no_of" ? "Cortes sem OF agrupados pelo início da descrição" : `OF ${d.ofs.join(" + ")}`} · {period(d.first_date, d.last_date)}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                [Scissors, "Cortes", d.cuts_count],
                [Timer, "T. corte", fmtMin(d.cutting_time)],
                [PauseCircle, "Paragem", fmtMin(d.machine_stopped)],
                [Ruler, "Perímetro", fmtM(d.perimeter)],
                [CalendarDays, "Dias", d.days_count],
              ].map(([Icon, l, v]) => (
                <div key={l} className="rounded-lg border p-3">
                  <div className="text-[11px] uppercase text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" />{l}</div>
                  <div className="text-lg font-semibold tabular-nums mt-0.5">{v}</div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border p-4 space-y-3" data-testid="po-goal-card">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="po-target">Objetivo · metros previstos</Label>
                  <div className="flex items-center gap-2">
                    <Input id="po-target" data-testid="po-target-input" inputMode="decimal" className="w-[160px]" placeholder="ex.: 1500" value={target} onChange={(e) => setTarget(e.target.value)} />
                    <span className="text-sm text-muted-foreground">m</span>
                  </div>
                </div>
                <div className="flex-1 min-w-[240px] space-y-1">
                  {d.target_perimeter ? (
                    <>
                      <ProgressBar pct={d.progress_pct} big testid="po-detail-progress" />
                      <div className="text-xs text-muted-foreground" data-testid="po-detail-progress-text">
                        {fmtM(d.perimeter)} cortados de {fmtM(d.target_perimeter)} ·{" "}
                        {d.remaining_perimeter > 0 ? `faltam ${fmtM(d.remaining_perimeter)}` : `objetivo atingido (+${fmtM(d.perimeter - d.target_perimeter)})`}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">Defina os metros previstos e carregue em Guardar para ver a percentagem já cortada.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3 rounded-lg border bg-muted/30 p-4">
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="po-name">Nome amigável</Label>
                <div className="flex gap-2">
                  <Input id="po-name" data-testid="po-name-input" placeholder={d.default_name} value={name} onChange={(e) => setName(e.target.value)} />
                  {d.custom_name && (
                    <Button variant="outline" size="icon" title="Repor nome original" data-testid="po-name-reset" onClick={() => save({ name: "" })}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">Todos os cortes deste grupo ({d.default_name}) passam a aparecer com este nome.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="po-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <Label htmlFor="po-notes">Notas</Label>
                <Textarea id="po-notes" data-testid="po-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Cliente, quantidades, observações…" />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button data-testid="po-save-button" onClick={() => save()} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Guardar
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <MiniTable title="Detalhe por peça" testid="po-pieces" rows={d.pieces} labelHead="Peça" />
              <MiniTable title="Por dia" testid="po-days" rows={d.days.map((x) => ({ ...x, label: fmtDate(x.label) }))} labelHead="Dia" />
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">Lista de cortes ({d.cuts.length})</h3>
              <div className="rounded-lg border overflow-x-auto max-h-[320px]">
                <table className="w-full text-xs" data-testid="po-cuts">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-muted-foreground">
                      <th className="text-left px-3 py-2">Data</th><th className="text-left px-2 py-2">Início</th><th className="text-left px-2 py-2">Fim</th>
                      <th className="text-left px-2 py-2">Peça</th><th className="text-right px-2 py-2 whitespace-nowrap">T. corte</th><th className="text-right px-2 py-2">Paragem</th>
                      <th className="text-right px-2 py-2">Perím.</th><th className="text-left px-3 py-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.cuts.map((c, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(c.date)}</td>
                        <td className="px-2 py-1.5 tabular-nums">{(c.start_time || "").slice(0, 8)}</td>
                        <td className="px-2 py-1.5 tabular-nums">{(c.end_time || "").slice(0, 8)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{c.piece}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtMin(c.cutting_time)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-red-600">{fmtMin(c.machine_stopped)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtM(c.perimeter)}</td>
                        <td className="px-3 py-1.5 text-muted-foreground max-w-[340px] truncate" title={c.description}>{c.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniTable({ title, rows, labelHead, testid }) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-xs" data-testid={testid}>
          <thead className="bg-muted/50 text-muted-foreground">
            <tr><th className="text-left px-3 py-2">{labelHead}</th><th className="text-right px-2 py-2">Cortes</th><th className="text-right px-2 py-2">T. corte</th><th className="text-right px-2 py-2">Paragem</th><th className="text-right px-3 py-2">Perím.</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t">
                <td className="px-3 py-1.5 font-medium">{r.label}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.cuts_count}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtMin(r.cutting_time)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-red-600">{fmtMin(r.machine_stopped)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtM(r.perimeter)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
